import {useCallback, useEffect, useState} from 'react'
import {Heading} from '@/components/tailwind/heading'
import {Birthday, getBirthdays} from '@/services/birthday-services'
import {useAuth} from '@/context/use-auth'
import {useReportsTranslation} from '@/i18n/hooks'
import {useToast} from '@/hooks/use-toast'
import {Button} from '@/components/tailwind/button'
import {Printer, FileSpreadsheet} from 'lucide-react'
import BirthdayFilters, {FilterState} from './components/birthday-filters'
import BirthdayTable from './components/birthday-table'
import log from '@/lib/logger'

const STORAGE_KEY = 'birthdayListFilter'

function getInitialFilterState(): FilterState {
    // Try to load from session storage
    try {
        const stored = sessionStorage.getItem(STORAGE_KEY)
        if (stored) {
            return JSON.parse(stored)
        }
    } catch {
        // Ignore errors, use default
    }

    // Default to current month
    const now = new Date()
    return {
        type: 'month',
        year: now.getFullYear(),
        month: now.getMonth() + 1, // Date.getMonth() is 0-based
    }
}

function BirthdayListPage() {
    const [birthdays, setBirthdays] = useState<Birthday[]>([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)
    const [filterState, setFilterState] = useState<FilterState>(getInitialFilterState)
    const {token} = useAuth()
    const {t} = useReportsTranslation()
    const {toast} = useToast()

    // Save filter state to session storage whenever it changes
    useEffect(() => {
        try {
            sessionStorage.setItem(STORAGE_KEY, JSON.stringify(filterState))
        } catch {
            // Ignore storage errors
        }
    }, [filterState])

    // Fetch birthdays when filter state changes
    const fetchBirthdays = useCallback(async () => {
        if (!token) {
            log.error('No token available for fetching birthdays')
            setLoading(false)
            return
        }

        try {
            setLoading(true)
            setError(null)

            const data = await getBirthdays(
                token,
                filterState.type,
                filterState.year,
                filterState.month,
                filterState.quarter
            )

            setBirthdays(data)
        } catch (err) {
            log.error('Error fetching birthdays:', err)
            setError(t('birthdays.error'))
        } finally {
            setLoading(false)
        }
    }, [filterState.type, filterState.year, filterState.month, filterState.quarter, token])

    useEffect(() => {
        fetchBirthdays()
    }, [fetchBirthdays])

    function handleFilterChange(newState: FilterState) {
        setFilterState(newState)
    }

    async function handleExportPdf() {
        if (!token) return

        try {
            const apiUrl = import.meta.env.VITE_NBH_URL_ROOT || import.meta.env.VITE_API_URL || 'http://localhost:8080'
            const params = new URLSearchParams({
                filterType: filterState.type,
                year: filterState.year.toString(),
            })

            if (filterState.type === 'month' && filterState.month) {
                params.append('month', filterState.month.toString())
            } else if (filterState.type === 'quarter' && filterState.quarter) {
                params.append('quarter', filterState.quarter.toString())
            }

            const response = await fetch(
                `${apiUrl}/api/birthdays/export/pdf?${params}`,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                }
            )

            if (!response.ok) {
                throw new Error('PDF download failed')
            }

            const blob = await response.blob()
            const url = window.URL.createObjectURL(blob)
            const a = document.createElement('a')

            // Generate filename based on filter
            let filename = 'Geburtstagsliste'
            if (filterState.type === 'month' && filterState.month) {
                const monthNames = ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
                                   'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember']
                filename += `_${monthNames[filterState.month - 1]}_${filterState.year}`
            } else if (filterState.type === 'quarter' && filterState.quarter) {
                filename += `_Q${filterState.quarter}_${filterState.year}`
            } else if (filterState.type === 'year') {
                filename += `_${filterState.year}`
            }
            filename += '.pdf'

            a.href = url
            a.download = filename
            document.body.appendChild(a)
            a.click()
            document.body.removeChild(a)
            window.URL.revokeObjectURL(url)
        } catch (error) {
            log.error('Error downloading PDF:', error)
            toast({
                title: t('birthdays.export.error'),
                description: t('birthdays.export.errorDescription'),
                variant: 'destructive'
            })
        }
    }

    async function handleExportExcel() {
        if (!token) return

        try {
            const apiUrl = import.meta.env.VITE_NBH_URL_ROOT || import.meta.env.VITE_API_URL || 'http://localhost:8080'
            const params = new URLSearchParams({
                filterType: filterState.type,
                year: filterState.year.toString(),
            })

            if (filterState.type === 'month' && filterState.month) {
                params.append('month', filterState.month.toString())
            } else if (filterState.type === 'quarter' && filterState.quarter) {
                params.append('quarter', filterState.quarter.toString())
            }

            const response = await fetch(
                `${apiUrl}/api/birthdays/export/excel?${params}`,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                }
            )

            if (!response.ok) {
                throw new Error('Excel download failed')
            }

            const blob = await response.blob()
            const url = window.URL.createObjectURL(blob)
            const a = document.createElement('a')

            // Generate filename based on filter
            let filename = 'Geburtstagsliste'
            if (filterState.type === 'month' && filterState.month) {
                const monthNames = ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
                                   'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember']
                filename += `_${monthNames[filterState.month - 1]}_${filterState.year}`
            } else if (filterState.type === 'quarter' && filterState.quarter) {
                filename += `_Q${filterState.quarter}_${filterState.year}`
            } else if (filterState.type === 'year') {
                filename += `_${filterState.year}`
            }
            filename += '.xlsx'

            a.href = url
            a.download = filename
            document.body.appendChild(a)
            a.click()
            document.body.removeChild(a)
            window.URL.revokeObjectURL(url)
        } catch (error) {
            log.error('Error downloading Excel:', error)
            toast({
                title: t('birthdays.export.error'),
                description: t('birthdays.export.errorDescription'),
                variant: 'destructive'
            })
        }
    }

    return (
        <div className="max-w-6xl mx-auto">
            <div className="mb-8 flex items-start justify-between">
                <div>
                    <Heading level={1}>{t('birthdays.title')}</Heading>
                    <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                        {t('birthdays.description')}
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <Button
                        plain
                        onClick={handleExportPdf}
                        className="flex items-center gap-2"
                        title={t('birthdays.export.pdf')}
                    >
                        <Printer className="h-4 w-4" />
                        <span className="hidden sm:inline">{t('birthdays.export.pdf')}</span>
                    </Button>
                    <Button
                        plain
                        onClick={handleExportExcel}
                        className="flex items-center gap-2"
                        title={t('birthdays.export.excel')}
                    >
                        <FileSpreadsheet className="h-4 w-4" />
                        <span className="hidden sm:inline">{t('birthdays.export.excel')}</span>
                    </Button>
                </div>
            </div>

            <BirthdayFilters
                filterState={filterState}
                onFilterChange={handleFilterChange}
            />

            {/* Error Message */}
            {error && (
                <div className="mb-6 p-4 bg-red-50 dark:bg-red-950 border-l-4 border-red-400 dark:border-red-500">
                    <p className="text-red-700 dark:text-red-300">{error}</p>
                </div>
            )}

            <BirthdayTable
                birthdays={birthdays}
                loading={loading}
                resetPageKey={`${filterState.type}|${filterState.year}|${filterState.month ?? ''}|${filterState.quarter ?? ''}`}
            />
        </div>
    )
}

export default BirthdayListPage
